import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'apps/piqet-angular/src/environments/environment';
import { Observable } from 'rxjs';

// Scope 3 Category 1 (upstream packaging) footprint report.
//
// The report is ORGANIZATION-wide and driven by a reporting year, not by a single
// project: the unit of disclosure is the SKU, and an organization's SKUs live
// across many projects, so one report routinely spans several. The organization
// comes from the JWT — there is no organizationId parameter anywhere.
//
// Both routes are read-only; nothing is ever saved. Every id sent back comes
// straight off the form data, so the front end never translates one enum into
// another. Numbers arrive as full-precision decimals — round for display only.
//
// Mirrors Scope3ReportModels.cs in the backend.

/** Standard PIQET envelope. On failure `data` is null and `message` carries the text. */
export interface Scope3Envelope<T> {
  data: T;
  message: string;
  messageType: number;
  error: any;
}

// --- Form data -------------------------------------------------------------

export interface Scope3OrganizationInfo {
  organizationId: number;
  organizationName: string;
  /** How many SKUs are linked to a scenario at all, before the year filter. */
  totalSkuCount: number;
  /** How many of those have a recorded volume for the selected year. */
  skuWithVolumeCount: number;
}

/** A single object, not a list — Category 1 is the only preset in this build. */
export interface Scope3Preset {
  id: number;
  name: string;
  ghgMapping: string;
  boundary: string;
  allocation: string;
  description: string;
}

export interface Scope3StatusOption {
  id: number;
  name: string;
  /** True for Completed — selected when the page first loads. */
  isDefault: boolean;
}

/** One SKU: its scenario, the project that owns it, and the volume recorded for the year. */
export interface Scope3ScenarioOption {
  scenarioId: number;
  code: string;
  name: string;
  projectId: number;
  projectCode: string;
  projectName: string;
  statusId: number;
  statusName: string;
  isDraft: boolean;
  skuCode: string | null;
  /** Null where the customer has not linked the SKU yet — show an input, never a zero. */
  uuid: string | null;
  department: string | null;
  category: string | null;
  subCategory: string | null;
  /** Units recorded for the selected year, or null when none is recorded. */
  unitsForYear: number | null;
  hasVolumeForYear: boolean;
}

export interface Scope3Indicator {
  id: number;
  name: string;
  unit: string;
}

export interface Scope3LookupOption {
  id: string;
  name: string;
}

/**
 * A scenario with a volume for the year that no SKU record points at. Reported
 * rather than ignored: the legacy organization report finds its scenarios through
 * ConfigurationYear alone, so these WOULD be counted there and would silently
 * vanish here. Surfacing them is what stops the two reports quietly disagreeing.
 */
export interface Scope3UnlinkedScenario {
  scenarioId: number;
  scenarioCode: string;
  scenarioName: string;
  projectCode: string;
  projectName: string;
  unitsForYear: number;
  message: string;
}

export interface Scope3Defaults {
  indicatorId: number;
  year: number;
  granularity: string;
  groupBy: string;
  subGroupBy: string;
  includeDraft: boolean;
  /** The SKUs with a volume for the year — pre-tick these. */
  selectedScenarioIds: number[];
}

export interface Scope3FormData {
  organization: Scope3OrganizationInfo;
  preset: Scope3Preset;
  /** Current year first, then the nine before it. */
  years: number[];
  selectedYear: number;
  scenarioStatuses: Scope3StatusOption[];
  /** Echoes the filter this response was built with. */
  includeDraft: boolean;
  scenarios: Scope3ScenarioOption[];
  scenariosWithoutSkuRecord: Scope3UnlinkedScenario[];
  indicators: Scope3Indicator[];
  granularities: Scope3LookupOption[];
  /** Primary roll-up dimension — department, category, sub-category or SKU. */
  groupByOptions: Scope3LookupOption[];
  /** Second level within each group — packaging component or lifecycle stage. */
  subGroupByOptions: Scope3LookupOption[];
  defaults: Scope3Defaults;
}

// --- Generate --------------------------------------------------------------

export interface Scope3VolumeOverride {
  scenarioId: number;
  units: number;
}

export interface Scope3GenerateRequest {
  /** Comma-separated scenario ids. May span several projects. */
  scenarioIds: string;
  indicatorId: number;
  /** `component` or `stage` — from `granularities[].id`. */
  granularity: string;
  /** Roll-up dimension for `groups` — from `groupByOptions[].id`. */
  groupBy: string;
  /** Second level within each group — from `subGroupByOptions[].id`. */
  subGroupBy: string;
  period: { year: number };
  /**
   * Overrides the stored ConfigurationYear volumes for this call only. Nothing
   * is persisted. Omit to use what is already recorded against the SKU.
   */
  volumes?: Scope3VolumeOverride[];
  /** Send true only when the export section is open — it makes the payload much larger. */
  includeCsv?: boolean;
}

/** The A-factor each project in scope actually ran with. Never "n/a". */
export interface Scope3ProjectAllocation {
  projectId: number;
  projectCode: string;
  projectName: string;
  allocationForRecycling: number;
}

export interface Scope3Meta {
  presetName: string;
  ghgMapping: string;
  boundary: string;
  /** A list, not one number, because a report can span projects that disagree. */
  aFactorByProject: Scope3ProjectAllocation[];
  aFactorSource: string;
  indicatorId: number;
  indicatorName: string;
  indicatorUnit: string;
  granularity: string;
  period: string;
  projectCount: number;
  /** Always false — the preset re-scopes the report only. */
  projectModified: boolean;
}

/** Traceability stamp. Goes into the CSV header too, not just the screen. */
export interface Scope3Stamp {
  reportId: string;
  organization: string;
  preset: string;
  ghgMapping: string;
  boundary: string;
  /** Rendered from every project in scope, so a mixed-allocation report cannot look uniform. */
  aFactor: string;
  databaseVersion: string;
  masterDataVersionId: number;
  piqetRelease: string;
  period: string;
  generatedAtUtc: string;
  generatedBy: string;
  calculationEngine: string;
}

export interface Scope3Summary {
  /** SKUs in scope. */
  scenariosInScope: number;
  componentsInScope: number;
  projectsInScope: number;
  totalUnits: number;
  /** Volume-weighted kg CO2e per unit across everything in scope. */
  perUnitAverage: number;
  totalTonnesCO2e: number;
  coverageGapCount: number;
}

/** One row per scenario x component. Always populated. */
export interface Scope3Row {
  scenarioId: number;
  scenarioCode: string;
  scenarioName: string;
  projectId: number;
  projectCode: string;
  projectName: string;
  statusName: string;
  isDraft: boolean;
  skuCode: string | null;
  uuid: string | null;
  department: string | null;
  category: string | null;
  subCategory: string | null;
  componentId: number | null;
  componentName: string;
  units: number;
  perUnit: number;
  tonnesCO2e: number;
  sharePercent: number;
}

/** Populated only when `granularity === 'stage'`. */
export interface Scope3StageRow {
  scenarioId: number;
  scenarioCode: string;
  scenarioName: string;
  projectId: number;
  projectCode: string;
  projectName: string;
  componentName: string;
  lifecycleStage: string;
  subProcess: string;
  /** Transport legs are flagged so freight is allocated to Category 1 OR 4 — never both. */
  ghgCategoryHint: string;
  perUnit: number;
  tonnesCO2e: number;
  sharePercent: number;
}

/** Second level inside a group — a packaging component or a lifecycle stage. */
export interface Scope3SubGroup {
  name: string;
  tonnesCO2e: number;
  /** Share of the parent group, NOT of the whole report. */
  sharePercent: number;
}

/**
 * One roll-up bucket — a department, category, sub-category or SKU, largest
 * first. Rows whose dimension is blank are collected under "(Not assigned)"
 * rather than dropped, so the groups always sum to the report total. Charts are
 * drawn from this, never by grouping `rows` client-side.
 */
export interface Scope3Group {
  /** Which dimension produced this grouping. */
  key: string;
  /** The dimension's value, or `(Not assigned)`. */
  name: string;
  tonnesCO2e: number;
  units: number;
  perUnitAverage: number;
  /** Share of the whole report. */
  sharePercent: number;
  skuCount: number;
  subGroups: Scope3SubGroup[];
}

/**
 * The bar chart, pre-built server-side in the same `ChartOutput` contract the
 * organization report and GenerateChartReport already return. Feed it straight
 * into the existing `piqet-report-chart` component — do not re-derive it from
 * `rows`, or the chart can quietly disagree with the table beneath it.
 *
 * Values are in t CO2e (period totals), not the indicator's per-package unit.
 * `null` when there is nothing to plot.
 */
export interface Scope3Chart {
  chartId: string;
  /** `stacked-column` for this report. */
  type: string;
  title: string;
  unit: string;
  /** May be a percentage string such as `"75%"`. */
  height: string | number;
  opacity: number;
  isExport: boolean;
  stacked: boolean;
  colors: string[];
  xAxis: { label: string; categories: string[]; labelsEnabled?: boolean };
  yAxis: { label: string; gridLineWidth?: number };
  legend: { enabled: boolean; padding?: number };
  series: {
    key: string;
    label: string;
    color: string;
    borderColor: string;
    borderWidth: number;
    /** Full-precision values as strings, with each point's share of its bar. */
    data: { y: string | number; percentage: string | number }[];
  }[];
}

export interface Scope3AuditStage {
  lifecycleStage: string;
  subProcess: string;
  included: boolean;
  reason: string;
}

export interface Scope3AuditComponent {
  scenarioId: number;
  scenarioName: string;
  componentName: string;
  stages: Scope3AuditStage[];
}

export interface Scope3Reconciliation {
  scenarioId: number;
  scenarioName: string;
  /** Sum of the per-component values this report displays. */
  upstream: number;
  /** The same figure from the engine's own aggregate — a different code path. */
  upstreamFromEngine: number;
  endOfLife: number;
  excluded: number;
  sum: number;
  engineTotal: number;
  difference: number;
  passed: boolean;
}

/** A SKU deliberately left out of the total. Never counted as zero, never an error. */
export interface Scope3CoverageGap {
  scenarioId: number;
  scenarioCode: string;
  scenarioName: string;
  /** `NoVolumeForPeriod` or `NoUuid`. */
  reason: string;
  message: string;
}

export type Scope3WarningSeverity = 'info' | 'warning' | 'blocking';

export interface Scope3Warning {
  /** `NO_VOLUME` | `NO_UUID` | `DRAFT_SCENARIO_INCLUDED` | `MIXED_ALLOCATION` | `RECONCILIATION_FAILED`. */
  code: string;
  severity: Scope3WarningSeverity;
  message: string;
}

export interface Scope3ReportResult {
  meta: Scope3Meta;
  stamp: Scope3Stamp;
  summary: Scope3Summary;
  /** Server-side roll-up. Feeds the summary table beside the chart. */
  groups: Scope3Group[];
  /** The same roll-up pre-rendered as a chart. Null when there is nothing to plot. */
  chart: Scope3Chart | null;
  rows: Scope3Row[];
  stageRows: Scope3StageRow[];
  audit: Scope3AuditComponent[];
  reconciliation: Scope3Reconciliation[];
  coverage: Scope3CoverageGap[];
  warnings: Scope3Warning[];
  /** Only when `includeCsv` was set and every scenario reconciled. */
  csv: string | null;
  csvFileName: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class Scope3ReportService {
  apiUrl = environment.url;

  constructor(private http: HttpClient) {}

  /**
   * Renders the whole set-up screen in one round trip. The SKU list and the
   * volumes are year-specific, so changing the year or the draft filter means
   * re-fetching this — not re-generating.
   *
   * @param year Omit for the current year.
   * @param includeDraft False lists completed scenarios only, which is the default.
   */
  getFormData(
    year?: number | null,
    includeDraft = false
  ): Observable<Scope3Envelope<Scope3FormData>> {
    let params = new HttpParams().set('includeDraft', includeDraft);
    if (year !== null && year !== undefined) {
      params = params.set('year', year);
    }
    return this.http.get<Scope3Envelope<Scope3FormData>>(`${this.apiUrl}/Scope3Report/FormData`, {
      params,
    });
  }

  /** One call recalculates everything — there is no partial-update endpoint. */
  generate(payload: Scope3GenerateRequest): Observable<Scope3Envelope<Scope3ReportResult>> {
    return this.http.post<Scope3Envelope<Scope3ReportResult>>(
      `${this.apiUrl}/Scope3Report/Generate`,
      payload
    );
  }
}
